
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Footer from '../../../components/Footer';
import {
    BookOpen,
    Award,
    Users,
    Globe,
    Target,
    Flag,
    GraduationCap,
    Briefcase,
    ArrowUpRight,
    Clock,
    Bookmark,
    Share2,
    Building,
    Rocket,
    Stars,
    Wrench,
    CheckCircle,
    MonitorPlay,
    ChevronRight,
    ArrowRight,
    Zap,
    Shield,
    Code,
    Database,
    Server,
    LucideSearch,
    Activity,
    MessageSquareQuote
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Title from '../../../components/TitleComponent';
import overview from '../../../assets/schools/computer/comp1.jpg';
import why from '../../../assets/schools/computer/comp2.jpg';
import dean from '../../../assets/schools/computer/dean_rb_singh.png';
import prashant from '../../../assets/schools/computer/prashant.jpg';
import sonu from '../../../assets/schools/computer/sonu.jpg';

// Program Data
const undergraduatePrograms = [
    {
        name: "Bachelor of Computer Applications",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    {
        name: "Bachelor of Computer Applications in AIML",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    {
        name: "Bachelor of Computer Applications in Data Science",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    {
        name: "Bachelor of Computer Applications in Cyber Security",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    {
        name: "B.Sc It ",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    {
        name: "B.Sc Computer Science",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    {
        name: "B.Tech(LE)",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    {
        name: "B.Tech CSE",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    {
        name: "B.Tech AI & ML",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    {
        name: "B.Tech Data Science",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    {
        name: "B.Tech Cyber Security",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    {
        name: "B.Tech. CS with IBM Associated Program (AIML, Data Science, Cyber Security & Forensics)",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    // {
    //     name: "Bachelor of Technology in Data Science and Engineering",
    //     path: "/programmes/ug-courses/engineering/btech/data-science-engineering"
    // },
    // {
    //     name: "Bachelor of Technology in Cyber Security and Engineering",
    //     path: "/programmes/ug-courses/engineering/btech/cybersecurity-engineering"
    // },
    // {
    //     name: "Bachelor of Technology in Artificial Intelligence Machine Learning and Engineering",
    //     path: "/programmes/ug-courses/engineering/btech/ai-ml"
    // },
    // {
    //     name: "Bachelor of Technology in Civil Engineering",
    //     path: "/programmes/ug-courses/engineering/btech/civil-engineering"
    // },
    // {
    //     name: "Bachelor of Technology in Computer Science and Engineering",
    //     path: "/programmes/ug-courses/engineering/btech/computer-science-engineering"
    // },
    // {
    //     name: "Bachelor of Technology in Mechanical Engineering",
    //     path: "/programmes/ug-courses/engineering/btech/mechanical-engineering"
    // },
];
const PostgraduatePrograms = [
    {
        name: "MCA AIML",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    {
        name: "MCA Data Science",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    {
        name: "MCA Cyber Security",
        path: "/api/programmes/ug-courses/engineering/bca"
    },
    // {
    //     name: "M.Tech CSE(AIML/ MCA Data Science/ MCA Cyber Security)",
    //     path: "/api/programmes/ug-courses/engineering/bca"
    // },

];

// Component for animated stats counter
const AnimatedCounter = ({ value, label, icon, color }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        const duration = 2000; // 2 seconds duration for the count animation
        const frameDuration = 1000 / 60; // 60fps
        const totalFrames = Math.round(duration / frameDuration);
        const increment = parseInt(value) / totalFrames;

        let currentFrame = 0;
        const counter = setInterval(() => {
            currentFrame++;
            setCount(Math.min(Math.round(increment * currentFrame), parseInt(value)));

            if (currentFrame === totalFrames) {
                clearInterval(counter);
            }
        }, frameDuration);

        return () => clearInterval(counter);
    }, [value]);

    return (
        <motion.div
            className={`${color} p-4 rounded-xl shadow-md hover:shadow-xl transition-all duration-300`}
            whileHover={{ scale: 1.02 }}
        >
            <div className="text-[#0e4d38]">{icon}</div>
            <h3 className="text-xl md:text-3xl font-bold mt-2 text-gray-800">{count}+</h3>
            <p className="text-gray-600 text-sm md:text-base">{label}</p>
        </motion.div>
    );
};

// Components
const HeroSection = () => (
    <div className="relative overflow-hidden bg-gradient-to-r from-[#0e4d38] to-[#23376c] text-white py-12 md:py-20 px-6 md:px-10 rounded-xl mb-8 md:mb-12">
        <div className="absolute inset-0 bg-[url('/api/placeholder/1600/800')] bg-cover bg-center opacity-10 mix-blend-overlay" aria-hidden="true"></div>
        <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl" aria-hidden="true"></div>
        <div className="absolute -left-20 -top-20 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl" aria-hidden="true"></div>

        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-6xl mx-auto relative z-10"
        >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">
                School of Computer Engineering & Applications
            </h1>
            <h2 className="text-xl md:text-2xl font-semibold mb-6 md:mb-8 text-green-100">
                Shaping Tomorrow's Digital Leaders at Maya Devi University
            </h2>

            <div className="flex flex-wrap gap-4 md:gap-6">
                <Link to="https://admission.maya.edu.in/">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-white text-[#0e4d38] px-6 md:px-8 py-3 md:py-4 rounded-lg text-base md:text-lg font-semibold flex items-center gap-2 group shadow-lg"
                    >
                        Apply Now
                        <Rocket className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" />
                    </motion.button>
                </Link>
                {/* <Link to="#programs">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-transparent border-2 border-white text-white px-6 md:px-8 py-3 md:py-4 rounded-lg text-base md:text-lg font-semibold flex items-center gap-2 group"
                    >
                        Explore Programs
                        <ArrowRight className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" />
                    </motion.button>
                </Link> */}
            </div>
        </motion.div>
    </div>
);

const Stats = () => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 my-8 md:my-12 px-4"
    >
        <AnimatedCounter
            icon={<Users className="w-8 h-8 md:w-10 md:h-10" />}
            value="8"
            label="Programs Offered"
            color="bg-blue-50"
        />
        <AnimatedCounter
            icon={<Award className="w-8 h-8 md:w-10 md:h-10" />}
            value="1000"
            label="Students Placed"
            color="bg-green-50"
        />
        <AnimatedCounter
            icon={<Briefcase className="w-8 h-8 md:w-10 md:h-10" />}
            value="48"
            label="Highest Package (LPA)"
            color="bg-purple-50"
        />
        <AnimatedCounter
            icon={<Globe className="w-8 h-8 md:w-10 md:h-10" />}
            value="100"
            label="Industry Partners"
            color="bg-orange-50"
        />
    </motion.div>
);

const SchoolOverview = () => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-white p-8 rounded-xl shadow-lg mb-12"
    >
        <div className="flex flex-col lg:flex-row gap-8 items-center">
            <div className="w-full lg:w-1/2">
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-[#0e4d38] rounded-lg text-white">
                        <BookOpen className="w-6 h-6" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-[#0e4d38]">School Overview</h2>
                </div>
                <div className="space-y-4 text-gray-700 text-lg">
                    <p>The School of Computer Applications at Maya Devi University is dedicated to shaping the future of technology by blending academic excellence with ethical responsibility. Anchored in a vision to be a leading center of innovation and knowledge, the School empowers students to become globally responsible digital leaders—equipped with technical expertise, creative problem-solving skills, and a strong moral compass.</p>
                    <p>Our programs are designed to foster a dynamic learning environment that promotes
                        innovation, critical thinking, and lifelong learning. We seamlessly integrate
                        emerging technologies with core human values to ensure our graduates are not
                        only proficient in cutting-edge tools but also driven by integrity, compassion, and a
                        commitment to social good.
                    </p>
                    <p>Through a global perspective, hands-on experiences, and a focus on sustainability
                        and inclusivity, the School prepares students to address real-world challenges and
                        shape a better, tech-enabled future for all.

                    </p>
                </div>
            </div>
            <div className="w-full lg:w-1/2 relative h-80 md:h-96 rounded-xl overflow-hidden shadow-xl">
                <img
                    src={overview}
                    alt="School of Computer Applications"
                    className="w-full h-full object-cover rounded-xl transition-transform duration-700 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0e4d38]/70 to-transparent flex items-end">
                    <div className="p-6">
                        <h3 className="text-white text-xl font-bold">Cutting-edge Learning Environment</h3>
                        <p className="text-white/80 text-sm">Modern classrooms and advanced computer labs</p>
                    </div>
                </div>
            </div>
        </div>
    </motion.div>
);

const WhyUsSection = () => {
    const features = [
        {
            icon: <Zap className="w-7 h-7" />,
            title: "Excellence in Education & Innovation",
            description: "We offer a future-ready curriculum that combines deep theoretical knowledge with practical, hands-on experience in cutting-edge technologies such as AI, cyber security, data science, cloud computing, and more."
        },
        {
            icon: <Shield className="w-7 h-7" />,
            title: "Ethics Meets Technology",
            description: "Our programs emphasize not just what you can build, but what you should build—instilling strong ethical values and social responsibility in every technologist we shape."
        },
        {
            icon: <Globe className="w-7 h-7" />,
            title: "Global Readiness",
            description: "We prepare you for a connected world with industry exposure, international collaborations, and real-world projects that enhance your global perspective and professional agility."
        },
        {
            icon: <LucideSearch className="w-7 h-7" />,
            title: "Research & Creativity",
            description: "Whether you're passionate about academic research or innovative product development, you'll have opportunities to explore, create, and solve problems that matter."
        },
        {
            icon: <Users className="w-7 h-7" />,
            title: "Supportive, Inclusive Environment",
            description: "We foster a diverse and inclusive learning culture where every voice is heard and every student is empowered to grow—personally, academically, and professionally."
        },
        {
            icon: <Activity className="w-7 h-7" />,
            title: "Commitment to Lifelong Learning",
            description: "In a fast-evolving tech world, we help you build not just a degree, but a mindset of continuous growth, critical thinking, and lifelong curiosity."
        }
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white p-8 rounded-xl shadow-lg mb-12"
        >
            <div className="flex flex-col md:flex-row gap-8">
                <div className="w-full md:w-1/3">
                    <div className="sticky top-24">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-[#0e4d38] rounded-lg text-white">
                                <CheckCircle className="w-6 h-6" />
                            </div>
                            <h2 className="text-2xl md:text-3xl font-bold text-[#0e4d38]">Why Choose Us?</h2>
                        </div>
                        <p className="text-gray-700 mb-6">Choosing the right place to pursue your education is a life-changing decision. Here's why the School of Computer Applications at Maya Devi University stands out:</p>
                        <div className="relative h-60 md:h-80 rounded-lg overflow-hidden">
                            <img
                                src={why}
                                alt="Students collaborating"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0e4d38] to-transparent p-4">
                                <p className="text-white font-semibold">Ready to become a responsible, visionary leader in tech? Join us—and build the future with purpose.</p>
                            </div>
                        </div>
                        <div className="mt-6">
                            <Link to="https://admission.maya.edu.in/">
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="w-full bg-[#0e4d38] text-white px-6 py-3 rounded-lg text-lg font-semibold flex items-center justify-center gap-2"
                                >
                                    Apply Now
                                    <ArrowRight className="w-5 h-5" />
                                </motion.button>
                            </Link>
                        </div>
                    </div>
                </div>
                <div className="w-full md:w-2/3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                            >
                                <div className="text-[#0e4d38] mb-4">
                                    <div className="p-3 bg-[#0e4d38]/10 rounded-lg inline-block">
                                        {feature.icon}
                                    </div>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-gray-800">{feature.title}</h3>
                                <p className="text-gray-600">{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const TopRecruiters = () => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-white p-8 rounded-xl shadow-lg mb-12"
    >
        <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-[#0e4d38] rounded-lg text-white">
                <Briefcase className="w-6 h-6" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0e4d38]">Top Recruiters</h2>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item, index) => (
                <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-gray-50 p-4 rounded-lg flex items-center justify-center h-24 shadow-sm hover:shadow-md transition-all duration-300"
                >
                    <img
                        src={`/api/placeholder/200/80`}
                        alt={`Company logo ${item}`}
                        className="max-h-16 max-w-full object-contain"
                    />
                </motion.div>
            ))}
        </div>
    </motion.div>
);

const ClubsAndSocieties = () => {
    const clubs = [
        {
            name: "AI & Robotics Society",
            icon: <Rocket className="w-6 h-6" />,
            color: "bg-blue-50"
        },
        {
            name: "CyberSec Club",
            icon: <Shield className="w-6 h-6" />,
            color: "bg-red-50"
        },
        {
            name: "Web & App Developers Hub",
            icon: <Code className="w-6 h-6" />,
            color: "bg-green-50"
        },
        {
            name: "Data Verse – Data Science Community",
            icon: <Database className="w-6 h-6" />,
            color: "bg-purple-50"
        },
        {
            name: "Tech4Good Society",
            icon: <Globe className="w-6 h-6" />,
            color: "bg-teal-50"
        },
        {
            name: "TechTalks & Toastmasters",
            icon: <Users className="w-6 h-6" />,
            color: "bg-amber-50"
        },
        {
            name: "Game Dev & AR/VR Club",
            icon: <Server className="w-6 h-6" />,
            color: "bg-indigo-50"
        }
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white p-8 rounded-xl shadow-lg mb-12"
        >
            <div className="flex items-center gap-3 mb-8">
                <div className="p-3 bg-[#0e4d38] rounded-lg text-white">
                    <Users className="w-6 h-6" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#0e4d38]">Clubs & Societies</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {clubs.map((club, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className={`${club.color} p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300`}
                    >
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-white rounded-lg shadow-md">
                                {club.icon}
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800">{club.name}</h3>
                        </div>
                    </motion.div>
                ))}
            </div>
        </motion.div>
    );
};

const DeansWelcome = () => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-white p-8 rounded-xxl shadow-lg mb-12"
    >
        <div className="flex flex-col md:flex-row gap-8">
            <div className="w-full md:w-1/3">
                <div className="rounded-xl overflow-hidden shadow-xl">
                    <img
                        src={dean}
                        alt="Dean of School of Computer Applications"
                        className="w-full h-auto"
                    />
                </div>
            </div>
            <div className="w-full md:w-2/3">
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-[#0e4d38] rounded-lg text-white">
                        <Award className="w-6 h-6" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-[#0e4d38]">Dean's Welcome Note</h2>
                </div>
                <div className="space-y-4 text-gray-700">
                    <p className="text-lg italic">"Dear Students, Faculty, and Visitors,"</p>
                    <p>It is my pleasure to welcome you to the School of Computer Science and
                        Applications at Maya Devi University—a place where innovation meets integrity,
                        and technology is shaped with purpose.
                    </p>
                    <p>In today’s rapidly evolving digital world, we believe that technical excellence must
                        go hand in hand with ethical responsibility and human values. Our School is
                        committed to empowering students not just with the latest skills and knowledge in
                        computer science, but with the creativity, critical thinking, and moral compass
                        needed to lead with impact on a global scale.
                    </p>
                    <p>Here, you will find a vibrant academic environment that nurtures innovation,
                        fosters interdisciplinary learning, and encourages lifelong curiosity. Whether you
                        aspire to become a software engineer, a researcher, an entrepreneur, or a socially
                        conscious tech leader, we are here to support your journey and help you thrive in a
                        future defined by change.
                    </p>
                    <p>We invite you to explore, learn, collaborate, and grow with us as we work together
                        to shape a more inclusive, sustainable, and technologically empowered world.

                    </p>
                    <p className="font-semibold">Together, let's build technology that makes our world better.</p>
                    <p className="font-bold text-[#0e4d38]">Warm regards,</p>
                    <p className="font-bold text-[#0e4d38]">Prof. Dr. R B Singh</p>
                    <p className="text-gray-600">Dean, School of Computer Science and Applications<br />Maya Devi University</p>
                </div>
            </div>
        </div>
    </motion.div>
);

const CourseCard = ({ course, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        className="group w-full"
    >
        <Link to={course.path}>
            <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 h-full flex flex-col justify-between gap-3">
                <div>
                    <div className="flex justify-between items-start">
                        <div className="p-3 bg-blue-50 rounded-lg">
                            <GraduationCap className="w-5 h-5 text-blue-600" />
                        </div>
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            className="p-2 rounded-full hover:bg-gray-100"
                        >
                            <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-[#0e4d38]" />
                        </motion.div>
                    </div>
                    <h3 className="text-lg font-semibold mt-3 text-gray-800 group-hover:text-[#0e4d38] transition-colors line-clamp-2">
                        {course.name}
                    </h3>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {/* <span>{course.name.includes('Master') ? '2 Years' : '4 Years'}</span> */}
                    </div>
                    <div className="flex items-center gap-1">
                        <Bookmark className="w-4 h-4" />
                        {/* <span>{course.name.includes('Master') ? 'PG' : 'UG'}</span> */}
                    </div>
                </div>
            </div>
        </Link>
    </motion.div>
);

const ProgramsSection = () => (
    <motion.div
        id="programs"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-white p-8 rounded-xl shadow-lg mb-12"
    >
        <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-[#0e4d38] rounded-lg text-white">
                <GraduationCap className="w-6 h-6" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0e4d38]">Our Programs</h2>
        </div>

        <div>
            <h4 className="text-xl md:text-3xl font-bold text-[#0e4d38] mb-6">
                Undergraduate Programs
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {undergraduatePrograms.slice(0, 20).map((course, index) => (
                    <CourseCard key={index} course={course} index={index} />
                ))}
            </div>
        </div>

        <div className="mt-8">
            <h4 className="text-xl md:text-3xl font-bold text-[#0e4d38] mb-6">
                Postgraduate Programs
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {PostgraduatePrograms.slice(0, 6).map((course, index) => (
                    <CourseCard key={index} course={course} index={index} />
                ))}
            </div>
        </div>

    </motion.div>
);

const CTASection = () => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative overflow-hidden bg-gradient-to-r from-[#0e4d38] to-[#23376c] text-white py-12 px-8 rounded-xl mb-12"
    >
        <div className="absolute inset-0 bg-[url('/api/placeholder/1600/800')] bg-cover bg-center opacity-10 mix-blend-overlay"></div>
        <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl"></div>

        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Shape Your Digital Future?</h2>
                <p className="text-white/80 max-w-xl">Join the School of Computer Applications at Maya Devi University and become part of a community dedicated to innovation, excellence, and responsible technology leadership.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
                <Link to="https://admission.maya.edu.in/">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-white text-[#0e4d38] px-8 py-4 rounded-lg text-lg font-semibold flex items-center gap-2 shadow-lg whitespace-nowrap"
                    >
                        Apply Now
                        <Rocket className="w-5 h-5" />
                    </motion.button>
                </Link>
                {/* <Link to="#programs">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold flex items-center gap-2 whitespace-nowrap"
                    >
                        View Programs
                        <ChevronRight className="w-5 h-5" />
                    </motion.button>
                </Link> */}
            </div>
        </div>
    </motion.div>
);


// 
const TestimonialCard = ({ student, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.2 }}
        className="bg-white rounded-xl shadow-md overflow-hidden flex flex-col h-full"
    >
        <div className="bg-gradient-to-r from-[#0e4d38]/10 to-[#23376c]/10 p-4">
            <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-gray-200 overflow-hidden">
                    <img
                        src={student.image}
                        alt={student.name}
                        className="w-full h-full object-cover"
                    />
                </div>
                <div>
                    <h3 className="font-bold text-lg text-gray-800">{student.name}</h3>
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Building className="w-3 h-3" />
                        <span>{student.company}</span>
                    </div>
                </div>
            </div>
        </div>

        <div className="p-6 flex-1 flex flex-col">
            <div className="text-[#0e4d38] mb-3">
                <MessageSquareQuote className="w-6 h-6" />
            </div>
            <p className="text-gray-700 italic flex-1">{student.testimonial}</p>

            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                {student.award && (
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Award className="w-4 h-4 text-[#0e4d38]" />
                        <span>{student.award}</span>
                    </div>
                )}
                <div className="text-sm text-gray-500">Class of {student.graduationYear}</div>
            </div>
        </div>
    </motion.div>
);

const StudentTestimonials = () => {
    const testimonials = [
        {
            name: "Tamanna",
            company: "Amazon",
            graduationYear: "2024",
            award: "Excellence Award",
            testimonial: "Studying at Maya Devi University has been one of the most transformative experiences of my life. The university not only provided a strong academic foundation but also fostered a culture of innovation, critical thinking, and continuous learning."
        },
        {
            name: "Prashant Bharti Rajak",
            company: "Nielsen",
            graduationYear: "2023",
            image: prashant,
            testimonial: "My university journey has been transformative, largely thanks to the supportive environment and excellent infrastructure. Access to well-equipped labs, smart classrooms, a comprehensive library, and high-speed internet kept me aligned with industry trends—playing a key role in securing my placement at Nielsen."
        },
        {
            name: "Sonu Thapa",
            company: "Ayavin",
            graduationYear: "2024",
            award: "Innovation Scholar",
            image: sonu,
            testimonial: "I am incredibly grateful to Maya Devi University for shaping me into the professional I am today. The knowledge, skills, and values I gained here played a vital role in helping me secure a position at Ayavin. Throughout my academic journey, I received constant support from my professors, access to industry-relevant coursework, and opportunities to engage in hands-on learning. The university's focus on practical application and real-world problem-solving gave me the confidence to take on challenges in a professional setting."
        }
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white p-8 rounded-xl shadow-lg mb-12"
        >
            <div className="flex items-center gap-3 mb-8">
                <div className="p-3 bg-[#0e4d38] rounded-lg text-white">
                    <Users className="w-6 h-6" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#0e4d38]">Student Testimonials</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((testimonial, index) => (
                    <TestimonialCard
                        key={index}
                        student={testimonial}
                        index={index}
                    />
                ))}
            </div>
        </motion.div>
    );
};

const SchoolOfComputerApplications = () => {
    return (
        <div className="min-h-screen bg-gray-50 font-poppins mt-[70px]">
            <Title canonicalPath="/programmes/ug-courses/school-of-computer-applications" />
            <Title title="School of Computer Applications | Maya Devi University" description="Explore cutting-edge computer application programs at Maya Devi University. Become a technology leader with our innovative curriculum, expert faculty, and industry partnerships." />

            <div className="max-w-7xl mx-auto pt-16 md:pt-20 pb-8 md:pb-16 px-4 space-y-6 md:space-y-8">
                <HeroSection />
                <Stats />
                <SchoolOverview />
                <WhyUsSection />
                {/* <TopRecruiters /> */}
                <ClubsAndSocieties />
                <DeansWelcome />
                <ProgramsSection />
                <StudentTestimonials />
                <CTASection />
            </div>
            <Footer />
        </div>
    );
};

export default SchoolOfComputerApplications;